/* Copyright (c) 2024 Xperi. All Rights Reserved. */

import { ContentType, OneLineGuideCell as IOneLineGuideCell } from 'client-code/grid-guide';

import { DimensionScaler } from '../device/DimensionScaler';
import {
    MOVIE_POSTER_PREFIX,
    OLG_EPISODIC_POSTER_IMAGE_HEIGHT,
    OLG_EPISODIC_POSTER_IMAGE_WIDTH,
    OLG_MOVIE_POSTER_IMAGE_HEIGHT,
    OLG_MOVIE_POSTER_IMAGE_WIDTH,
    POSTER_IMAGE_NAME,
} from '../strings';
import { DateTime } from '../utils';

export class OneLineGuideCell {
    /**
     * Collection ID for a given guide cell.
     * @type {string}
     */
    collectionId: string;

    /**
     * Content ID for a given guide cell.
     * @type {string}
     */
    contentId: string;

    /**
     * The type of content.
     * @type {ContentType}
     */
    contentType?: ContentType;

    /**
     * Showing duration in seconds.
     * @type {number}
     */
    duration: number;

    /**
     * When true, showing should be treated as adult.
     * @type {boolean}
     */
    isAdult?: boolean;

    /**
     * When true, showing should be treated as new.
     * @type {boolean}
     */
    isNew?: boolean;

    /**
     * When true, showing supports PPV.
     * @type {boolean}
     */
    isPPV?: boolean;

    /**
     * The year the movie was released.
     * @type {number}
     */
    movieYear?: number;

    /**
     * Offer ID for a given guide cell.
     * @type {string}
     */
    offerId: string;

    /**
     * Showing start time as Date object.
     * @type {Date}
     */
    startTime: DateTime;

    /**
     * Showing title.
     * @type {string}
     */
    title?: string;

    /**
     * When true, showing supports start-over.
     * @type {boolean}
     */
    isStartover?: boolean;

    /**
     * When true, showing supports catch-up.
     * @type {boolean}
     */
    isCatchup?: boolean;

    /**
     * When true, showing can be recorded.
     * @type {boolean}
     */
    isRecordable?: boolean;

    /**
     * Showing season number.
     * @type {number}
     */
    seasonNumber?: number;

    /**
     * Showing episode number.
     * @type {number}
     */
    episodeNumber?: number;

    /**
     * Showing subtitle.
     * @type {string}
     */
    subtitle?: string;

    /**
     * poster image url
     * @type {string}
     */
    posterImageUrl: string;

    constructor(oneLineGuideCell: IOneLineGuideCell, imageHostBaseUrl: string) {
        this.collectionId = oneLineGuideCell.collectionId;
        this.contentId = oneLineGuideCell.contentId;
        this.contentType = oneLineGuideCell.contentType;
        this.duration = oneLineGuideCell.duration;
        this.isAdult = oneLineGuideCell.isAdult;
        this.isNew = oneLineGuideCell.isNew;
        this.isPPV = oneLineGuideCell.isPPV;
        this.isRecordable = oneLineGuideCell.isRecordable;
        this.movieYear = oneLineGuideCell.movieYear;
        this.offerId = oneLineGuideCell.offerId;
        this.startTime = new DateTime(oneLineGuideCell.startTime * 1000);
        this.title = oneLineGuideCell.title;
        this.isRecordable = oneLineGuideCell.isRecordable;
        this.seasonNumber = oneLineGuideCell.seasonNumber;
        this.episodeNumber = oneLineGuideCell.episodeNumber;
        this.subtitle = oneLineGuideCell.subtitle;
        this.posterImageUrl = `${imageHostBaseUrl}/images/${oneLineGuideCell.collectionId}/${
            oneLineGuideCell.contentType === 'movie'
                ? `${MOVIE_POSTER_PREFIX}${DimensionScaler.getInstance().getScaledWidth(
                      OLG_MOVIE_POSTER_IMAGE_WIDTH,
                  )}x${DimensionScaler.getInstance().getScaledHeight(OLG_MOVIE_POSTER_IMAGE_HEIGHT)}`
                : `${POSTER_IMAGE_NAME}${DimensionScaler.getInstance().getScaledWidth(
                      OLG_EPISODIC_POSTER_IMAGE_WIDTH,
                  )}x${DimensionScaler.getInstance().getScaledHeight(OLG_EPISODIC_POSTER_IMAGE_HEIGHT)}`
        }`;
    }
}
